import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import {
  DtoV1PropertyTaxAdditionalInstallment,
  DtoV1PropertyTaxEscrowInstallment,
  DtoV1PropertyTaxSupplementalInstallment,
} from '~/__generated__/api';
import Alert from '~/common/componentMapper/Alert/Alert';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import type { LoanSummary } from '~/common/hooks/useGetLoanSummary';
import { Accordion } from '~/common/mui/MUIAccordion';

import { ESCROW_WAIVER_TAX_OPTION_IDS, TAX_PAYMENT_TYPES } from '../constants';
import { PADDED_TAX_ALERT } from '../language.json';
import { CollectAtClosing } from './CollectAtClosing';
import { PrimaryInstallments } from './PrimaryInstallments';

type PayeeCardProps = {
  payeeName: string;
  isExempt?: boolean;
  installments?: DtoV1PropertyTaxEscrowInstallment[];
  supplementalInstallments?: DtoV1PropertyTaxSupplementalInstallment[];
  supplementalTaxes?: DtoV1PropertyTaxEscrowInstallment[];
  additionalTaxes?: DtoV1PropertyTaxAdditionalInstallment[];
  isRefinance?: boolean;
  loanData?: LoanSummary;
  escrowWaiverOption?: number | null;
  supplementalDueAtClosing?: number | null;
  nonAdValorum?: number | null;
};

const sortByDate = (
  a: DtoV1PropertyTaxEscrowInstallment,
  b: DtoV1PropertyTaxEscrowInstallment,
) => {
  const dateA = new Date(a.distributionDate);
  const dateB = new Date(b.distributionDate);
  return dateA.getTime() - dateB.getTime();
};

const PAID_AT_CLOSING_PAYMENT_TYPES = new Set<
  DtoV1PropertyTaxAdditionalInstallment['paidType']
>([
  TAX_PAYMENT_TYPES.COLLECT_AT_CLOSING,
  TAX_PAYMENT_TYPES.BORROWER_TO_PAY_AT_CLOSING,
  TAX_PAYMENT_TYPES.SELLER_TO_PAY_AT_CLOSING,
]);

export const PayeeCard = ({
  payeeName,
  isExempt = false,
  installments,
  additionalTaxes,
  supplementalInstallments,
  supplementalTaxes: legacySupplementalTaxes,
  loanData,
  escrowWaiverOption,
  supplementalDueAtClosing = 0,
  nonAdValorum = 0,
}: Readonly<PayeeCardProps>) => {
  const nonAdValorumAmount = nonAdValorum ?? 0;
  const supplementalTaxes = useMemo<DtoV1PropertyTaxEscrowInstallment[]>(
    () =>
      supplementalInstallments
        ? supplementalInstallments.map(
            ({
              supplementalAmount,
              supplementalDate,
              taxId,
              ...installment
            }) => ({
              ...installment,
              taxId: taxId ?? '',
              installmentId: installment.supplementalId,
              installmentAmount: supplementalAmount ?? 0,
              distributionDate: supplementalDate ?? '',
              isSupplemental: true,
            }),
          )
        : (legacySupplementalTaxes ?? []).map((tax) => ({
            ...tax,
            isSupplemental: true,
          })),
    [legacySupplementalTaxes, supplementalInstallments],
  );
  const fifteenPercentPadding = 1.15;
  const isTaxEscrowWaived =
    !!escrowWaiverOption &&
    (ESCROW_WAIVER_TAX_OPTION_IDS as readonly number[]).includes(
      escrowWaiverOption,
    );
  const hasPastBillPayAtClosing = installments
    .concat(additionalTaxes)
    .some((tax) => tax.paidType === TAX_PAYMENT_TYPES.PAST_BILL_PAY_AT_CLOSING);
  const {
    allPrimaryInstallments: primaryInstallments,
    primaryInstallmentsTotal,
  } = useMemo(() => {
    const allPrimaryInstallments = installments
      .concat(
        additionalTaxes.filter(
          (tax) =>
            tax.isPastInstallment &&
            !PAID_AT_CLOSING_PAYMENT_TYPES.has(tax.paidType),
        ),
      )
      .concat(
        supplementalTaxes
          .filter((tax) => tax.paidType === TAX_PAYMENT_TYPES.COLLECT_IN_ESCROW)
          .map((tax) => tax),
      )
      .sort(sortByDate)
      .map((installment) =>
        installment.paidType === TAX_PAYMENT_TYPES.PAST_BILL_PAY_AT_CLOSING
          ? {
              ...installment,
              installmentAmount:
                installment.installmentAmount * fifteenPercentPadding,
            }
          : installment,
      );
    const primaryInstallmentsTotal =
      allPrimaryInstallments.reduce(
        (total, installment) =>
          isTaxEscrowWaived &&
          installment.paidType === TAX_PAYMENT_TYPES.NOT_ESCROWED
            ? total
            : total + installment.installmentAmount,
        0,
      ) + nonAdValorumAmount;

    return {
      allPrimaryInstallments,
      primaryInstallmentsTotal,
    };
  }, [
    installments,
    additionalTaxes,
    supplementalTaxes,
    isTaxEscrowWaived,
    nonAdValorumAmount,
  ]);

  const supplementalPaidAtClosingTotal = supplementalDueAtClosing ?? 0;

  const {
    allInstallmentsCollectedAtClosingTotal,
    delinquentTaxesTotal,
    allInstallmentsCollectedAtClosing,
  } = useMemo(() => {
    const { estimatedClosingDate, actualClosingDate, firstPaymentDate } =
      loanData.loanSummaryDates;
    const effectiveClosingDate = actualClosingDate ?? estimatedClosingDate;
    const allInstallments = installments
      .concat(supplementalTaxes)
      .concat(additionalTaxes)
      .sort(sortByDate);

    const delinquentTaxesInstallments = allInstallments.filter(
      (installment) => installment.paidType === TAX_PAYMENT_TYPES.DELINQUENT,
    );
    const delinquentTaxesTotal = delinquentTaxesInstallments.reduce(
      (total, installment) => total + installment.installmentAmount,
      0,
    );
    const allInstallmentsCollectedAtClosing = allInstallments
      .filter(
        (installment) =>
          installment.paidType === TAX_PAYMENT_TYPES.COLLECT_AT_CLOSING ||
          installment.paidType ===
            TAX_PAYMENT_TYPES.BORROWER_TO_PAY_AT_CLOSING ||
          installment.paidType === TAX_PAYMENT_TYPES.SELLER_TO_PAY_AT_CLOSING ||
          installment.paidType === TAX_PAYMENT_TYPES.PAST_BILL_PAY_AT_CLOSING ||
          installment.paidType ===
            TAX_PAYMENT_TYPES.CURRENT_BILL_PAY_AT_CLOSING,
      )
      .map((installment) => {
        const installmentDistributionDate = dayjs(installment.distributionDate);
        const isDueBetweenClosingAndFirstPaymentDate =
          firstPaymentDate?.isAfter(installmentDistributionDate) &&
          installmentDistributionDate.isAfter(effectiveClosingDate);
        return {
          ...installment,
          installmentAmount:
            installment.paidType === TAX_PAYMENT_TYPES.PAST_BILL_PAY_AT_CLOSING
              ? installment.installmentAmount * fifteenPercentPadding
              : installment.installmentAmount,
          isDueBetweenClosingAndFirstPaymentDate,
        };
      });

    const allInstallmentsCollectedAtClosingTotal =
      allInstallmentsCollectedAtClosing.reduce(
        (total, installment) => total + installment.installmentAmount,
        0,
      ) + supplementalPaidAtClosingTotal;

    return {
      delinquentTaxesTotal,
      allInstallmentsCollectedAtClosing,
      allInstallmentsCollectedAtClosingTotal,
    };
  }, [
    additionalTaxes,
    installments,
    supplementalTaxes,
    loanData,
    supplementalPaidAtClosingTotal,
  ]);

  return (
    <Accordion
      defaultExpanded={false}
      header={
        <Stack
          sx={{
            gap: 2,
            direction: 'row',
            justifyContent: 'space-between',
            flex: 1,
            paddingRight: 8,
          }}
        >
          <Text variant="h6">{payeeName} Taxes</Text>
          {isExempt && (
            <Chip
              color="info"
              label={'Borrower Exempt'}
              size="small"
              sx={{
                width: 'fit-content',
                height: '100%',
                color: 'black',
                backgroundColor: '#CEEDFF',
                paddingTop: '2px',
                fontSize: '12px',
                border: '1px solid #03A9F4',
              }}
            />
          )}
        </Stack>
      }
    >
      <PrimaryInstallments
        primaryInstallments={primaryInstallments}
        primaryInstallmentsTotal={primaryInstallmentsTotal}
        isTaxEscrowWaived={isTaxEscrowWaived}
        nonAdValorum={nonAdValorumAmount}
      />
      {(allInstallmentsCollectedAtClosing.length > 0 ||
        supplementalPaidAtClosingTotal > 0) && (
        <CollectAtClosing
          allInstallmentsCollectedAtClosing={allInstallmentsCollectedAtClosing}
          delinquentTaxesTotal={delinquentTaxesTotal}
          supplementalTaxesPaidAtClosing={supplementalPaidAtClosingTotal}
          allInstallmentsCollectedAtClosingTotal={
            allInstallmentsCollectedAtClosingTotal
          }
        />
      )}
      {hasPastBillPayAtClosing && (
        <Stack sx={{ width: '60%', p: '1rem 0 0 2rem' }}>
          <Alert severity="info" message={PADDED_TAX_ALERT} />
        </Stack>
      )}
    </Accordion>
  );
};
